'use client';

import type { ImageSourcePropType } from 'react-native';

import { Button, cn, Div, Image, Markdown, Span } from '@scatterlab/zeta-core';
import {
  ComponentPropsWithoutRef,
  ComponentRef,
  createElement,
  ElementType,
  memo,
  ReactNode,
  RefAttributes,
} from 'react';

import { DEFAULT_CHARACTER_PROFILE_IMAGE_URL } from '../constants';
import { Next16Icon } from '../icons';
import { MarkdownComponents } from '../markdownComponents';

export type RecommendedReplyCardProps<
  Component extends ElementType = typeof Div,
> = ComponentPropsWithoutRef<Component> &
  RefAttributes<ComponentRef<Component>> & {
    component?: Component;
    image?: string | ImageSourcePropType;
    imageUnoptimized?: boolean;
    creatorUsername: string;
    name: string;
    greeting?: string;
    messages: string[];
    onPressSend?: (message: string) => void;
    addon?: ReactNode;
  };

const RecommendedReplyCard = <Component extends ElementType = typeof Div>({
  component,
  image,
  imageUnoptimized,
  creatorUsername,
  name,
  messages,
  greeting,
  className,
  onPressSend,
  addon,
  ...props
}: RecommendedReplyCardProps<Component>) =>
  createElement(
    component ?? Div,
    {
      tabIndex: -1,
      ...props,
      className: cn(
        'flex max-w-[310px] flex-col rounded-xl border border-gray-50/5 bg-white/5 px-4 py-3 shadow-400',
        className,
      ),
    },
    <>
      <Div
        className={cn(
          'flex flex-row items-center gap-2 border-b border-b-gray-50/5 pb-[7px]',
          {
            'border-b-transparent': !greeting,
          },
        )}
      >
        <Image
          src={image ?? DEFAULT_CHARACTER_PROFILE_IMAGE_URL}
          alt={`${creatorUsername}의 ${name}`}
          width={30}
          height={30}
          className="size-[30px] rounded-full object-cover"
          unoptimized={imageUnoptimized}
        />
        <Span
          className="body14 line-clamp-1 min-w-0 flex-1 text-ellipsis text-gray-50"
          title={name}
        >
          {name}
        </Span>
        {addon}
      </Div>
      <Div className="my-2.5 flex h-[38px] w-full flex-row">
        {greeting && (
          <Markdown
            className="body16 line-clamp-2 text-ellipsis text-gray-300"
            components={MarkdownComponents}
          >
            {greeting.replaceAll('\n', ' ')}
          </Markdown>
        )}
      </Div>
      <Div className="flex flex-col gap-2.5">
        {messages.map((message: string) => (
          <Button
            className="flex h-[60px] flex-row items-center justify-between gap-2.5 rounded-[10px] border border-white/5 bg-gray-sub2 px-3 py-2 shadow-200 transition-colors hover:bg-white/20"
            key={message}
            onPress={() => onPressSend?.(message)}
          >
            <Span
              className="body14 line-clamp-2 shrink text-left"
              title={message}
            >
              {`"${message}"`}
            </Span>
            <Next16Icon className="size-4 text-white" />
          </Button>
        ))}
      </Div>
    </>,
  );

export default memo(RecommendedReplyCard) as typeof RecommendedReplyCard;
